import { Component, OnInit } from '@angular/core';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  NotificationPreferencesService,
  UserNotificationPreference,
  EventTypeInfo
} from '../../shared/services/notification-preferences.service';

declare var CURRENT_USER_ID: number;

interface PreferenceGroup {
  category: string;
  preferences: PreferenceItem[];
}

interface PreferenceItem {
  eventType: string;
  displayName: string;
  description: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
}

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.css'],
    standalone: false
})
export class SettingsComponent implements OnInit {
  loading = false;
  saving = false;
  preferenceGroups: PreferenceGroup[] = [];
  currentUserId = CURRENT_USER_ID;

  constructor(
    private preferencesService: NotificationPreferencesService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.loadPreferences();
  }

  loadPreferences(): void {
    this.loading = true;

    this.preferencesService.getCurrentUserPreferences().subscribe({
      next: (preferences) => {
        this.buildPreferenceGroups(preferences);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading preferences', err);
        // Initialize with default values if no preferences exist
        this.buildPreferenceGroups([]);
        this.loading = false;
      }
    });
  }

  buildPreferenceGroups(preferences: UserNotificationPreference[]): void {
    const eventTypesByCategory = this.preferencesService.getEventTypesByCategory();
    const preferencesMap = new Map(preferences.map(p => [p.eventType, p]));

    this.preferenceGroups = [];

    eventTypesByCategory.forEach((eventTypes, category) => {
      const group: PreferenceGroup = {
        category,
        preferences: eventTypes.map(et => {
          const pref = preferencesMap.get(et.eventType);
          return {
            eventType: et.eventType,
            displayName: et.displayName,
            description: et.description,
            emailEnabled: pref?.emailEnabled ?? true,
            inAppEnabled: pref?.inAppEnabled ?? true
          };
        })
      };
      this.preferenceGroups.push(group);
    });
  }

  onPreferenceChange(item: PreferenceItem, type: 'email' | 'inApp'): void {
    this.saving = true;

    const preference: UserNotificationPreference = {
      userId: this.currentUserId,
      eventType: item.eventType,
      emailEnabled: item.emailEnabled,
      inAppEnabled: item.inAppEnabled
    };

    this.preferencesService.updatePreference(preference).subscribe({
      next: () => {
        this.saving = false;
        this.showNotification('Preference saved', 'success');
      },
      error: (err) => {
        // Revert the change
        if (type === 'email') {
          item.emailEnabled = !item.emailEnabled;
        } else {
          item.inAppEnabled = !item.inAppEnabled;
        }
        this.saving = false;
        this.showNotification('Error saving preference', 'error');
      }
    });
  }

  enableAllEmail(): void {
    this.setAllPreferences('email', true);
  }

  disableAllEmail(): void {
    this.setAllPreferences('email', false);
  }

  enableAllInApp(): void {
    this.setAllPreferences('inApp', true);
  }

  disableAllInApp(): void {
    this.setAllPreferences('inApp', false);
  }

  private setAllPreferences(type: 'email' | 'inApp', enabled: boolean): void {
    this.preferenceGroups.forEach(group => {
      group.preferences.forEach(item => {
        if (type === 'email') {
          item.emailEnabled = enabled;
        } else {
          item.inAppEnabled = enabled;
        }
        this.savePreference(item);
      });
    });
  }

  private savePreference(item: PreferenceItem): void {
    const preference: UserNotificationPreference = {
      userId: this.currentUserId,
      eventType: item.eventType,
      emailEnabled: item.emailEnabled,
      inAppEnabled: item.inAppEnabled
    };

    this.preferencesService.updatePreference(preference).subscribe();
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      type: { style: type, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 400 },
      hideAfter: 2000
    });
  }
}
